import { isEqual } from 'lodash';
import React from 'react';
import { useState, useCallback, useMemo } from 'react';
import { useUnitsOnHandData } from 'src/components/BeaconV2Components/ExperimentalLayout/Summary/hooks/useUnitsOnHoldData';
import MetricCell from 'src/components/BeaconV2Components/MetricCell/MetricCell';
import { HeadColumnDefinition } from 'src/components/BeaconV2Components/MetricList/MetricListContainer';
import SlSkeleton from 'src/components/BeaconV2Components/SlSkeleton';
import { calculatePercentChange, getAppName } from 'src/utils/app';
import { INDEX_FIELDS } from 'src/utils/entityDefinitions';

interface ListContainerWithUnitsOnHoldProps {
  groupBy: string;
  children: (props: {
    onChangePage: (args: { page: number; chunkedPage: number; }) => void;
    onGroupByChange: () => void;
    postProcessData: (data: any[]) => any[];
    additionalColumnDefs: HeadColumnDefinition[];
    unitsOnHandRowsLoading: boolean;
  }) => React.ReactElement;
}

const ListContainerWithUnitsOnHold = ({ groupBy, children }: ListContainerWithUnitsOnHoldProps) => {
  const [page, setPage] = useState(1);
  const [mainTableData, setMainTableData] = useState<any[]>([]);
  const [entityIdList, setEntityIdList] = useState<string[]>([]);

  const {
    data: unitsOnHandData,
    isLoading: unitsOnHandRowsLoading
  } = useUnitsOnHandData(groupBy, entityIdList, page);

  const additionalColumnDefs = useMemo(() => {
    const columnFieldName = 'unitsOnHand';
    const columnField = INDEX_FIELDS.getField(getAppName(), 'sales', columnFieldName);

    return [{
      headerName: columnField.displayName,
      valueFormatter: (_, row) => {
        if (!row[columnFieldName] && unitsOnHandRowsLoading) {
          return <SlSkeleton height={12} width="100%" />;
        }

        const metricValue = row[columnFieldName]?.value;
        const metricDiff = row[columnFieldName]?.difference;

        return (
          <MetricCell
            value={metricValue}
            metricType={columnField.metricType}
            percentChange={calculatePercentChange(metricValue, metricValue - metricDiff)}
          />
        );
      }
    }];
  }, [unitsOnHandRowsLoading]);

  const getNewEntityIdList = (data: any[], currentUnitsOnHandData: any[]) => {
    const unitsOnHandDataEntityIdList = new Set<string>();
    currentUnitsOnHandData.forEach((row) => unitsOnHandDataEntityIdList.add(row[groupBy]));

    const result = [];
    data.forEach((row) => {
      if (!unitsOnHandDataEntityIdList.has(row[groupBy])) {
        result.push(row[groupBy]);
      }
    });

    return result;
  };

  const isTableDataUpdated = (data: any[]) => {
    const dataStr = data.map((row) => row[groupBy]).sort((a, b) => a - b);
    const tableDataStr = mainTableData.map((row) => row[groupBy]).sort((a, b) => a - b);
    return !isEqual(dataStr, tableDataStr);
  };

  const postProcessData = useCallback((data) => {
    if (isTableDataUpdated(data)) {
      setMainTableData(data);

      const newValues = getNewEntityIdList(data, unitsOnHandData);
      setEntityIdList(newValues);
    }

    if (unitsOnHandData.length) {
      const unitsOnHandDataMap = unitsOnHandData.reduce((acc, row) => {
        return {
          ...acc,
          [row[groupBy]]: row.unitsOnHand
        };
      }, {} as { [key: string]: number });

      const unitsOnHandRowsByDropdownOption = mainTableData.map((row) => {
        return {
          ...row,
          unitsOnHand: unitsOnHandDataMap[row[groupBy]]
        };
      });

      return unitsOnHandRowsByDropdownOption;
    }

    return data;
  }, [unitsOnHandData, mainTableData]);

  const onChangePage = useCallback(({ chunkedPage }) => {
    if (chunkedPage !== page) {
      setPage(chunkedPage);
      setEntityIdList([]);
    }
  }, [page]);

  const onGroupByChange = useCallback(() => {
    setEntityIdList([]);
    setPage(1);
    setMainTableData([]);
  }, [setEntityIdList, setPage, setMainTableData]);

  return children({
    onChangePage,
    onGroupByChange,
    postProcessData,
    additionalColumnDefs,
    unitsOnHandRowsLoading
  });
};

export default ListContainerWithUnitsOnHold;
